import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { ArrowRight } from "lucide-react";

export default function HomePage() {
  const [featured, setFeatured] = useState([]);

  useEffect(() => {
    api.get("/products", { params: { featured: true } }).then(r => setFeatured(r.data)).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="bg-brand-ink text-brand-bg" data-testid="hero-section">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 grid md:grid-cols-2 gap-10 md:gap-20 items-center py-16 md:py-24 min-h-[88vh]">
          <div className="animate-fade-up order-2 md:order-1">
            <div className="overline text-brand-gold mb-4">New Collection · 2026</div>
            <h1 className="font-serif text-5xl md:text-7xl tracking-tighter leading-[0.95] font-medium text-brand-bg">
              Worn boldly.<br />
              <span className="text-brand-gold italic">Kept forever.</span>
            </h1>
            <p className="mt-8 text-base md:text-lg text-brand-bg/85 max-w-lg leading-relaxed">
              Hand-tailored Ankara pieces from cindymaks Collections — celebrating heritage prints with an editorial cut.
            </p>
            <div className="mt-10 flex flex-wrap gap-6 items-center">
              <Link to="/shop" data-testid="hero-shop-btn" className="btn-primary" style={{ background: "#D4AF6A", color: "#0A0807" }}>
                Shop the collection
              </Link>
              <Link to="/about" data-testid="hero-about-btn" className="text-brand-bg text-sm uppercase tracking-[0.2em] underline underline-offset-8 hover:text-brand-gold">
                Our story
              </Link>
            </div>
          </div>
          <div className="order-1 md:order-2">
            <div className="aspect-[3/4] overflow-hidden">
              <img
                src="https://customer-assets.emergentagent.com/job_ankara-vibes/artifacts/6teneoxn_IMG-20260606-WA0018.jpg"
                alt=""
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* FEATURED */}
      <section className="py-20 md:py-32 px-6 md:px-12 max-w-[1400px] mx-auto" data-testid="featured-section">
        <div className="flex items-end justify-between mb-12">
          <div>
            <div className="overline">Featured</div>
            <h2 className="font-serif text-4xl md:text-5xl mt-3 tracking-tight">Pieces we love</h2>
          </div>
          <Link to="/shop" className="hidden md:inline-flex items-center gap-2 text-sm tracking-[0.18em] uppercase hover:text-brand-accent">
            View all <ArrowRight size={16} />
          </Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-8">
          {featured.map((p, idx) => (
            <Link
              key={p.id}
              to={`/product/${p.id}`}
              data-testid={`featured-product-${p.id}`}
              className="group cursor-pointer animate-fade-up"
              style={{ animationDelay: `${idx * 80}ms` }}
            >
              <div className="aspect-[3/4] overflow-hidden bg-brand-bg2">
                <img src={p.image} alt={p.name} className="product-img w-full h-full object-cover" />
              </div>
              <div className="mt-4">
                <h3 className="font-serif text-lg leading-tight">{p.name}</h3>
                <div className="text-sm text-brand-muted mt-1">£{p.price.toFixed(2)}</div>
              </div>
            </Link>
          ))}
          {featured.length === 0 && (
            <div className="col-span-full text-center py-16 text-brand-muted">
              <div className="font-serif text-2xl text-brand-ink mb-2">Your collection awaits</div>
              <div className="text-sm">Sign in to admin to upload your first pieces.</div>
            </div>
          )}
        </div>
      </section>

      {/* ABOUT TEASER */}
      <section className="bg-brand-bg2 py-20 md:py-32" data-testid="about-teaser">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 grid md:grid-cols-2 gap-12 md:gap-20 items-center">
          <div className="aspect-[4/5] overflow-hidden">
            <img
              src="https://customer-assets.emergentagent.com/job_ankara-vibes/artifacts/xkfovu5m_IMG-20260606-WA0019.jpg"
              alt="Founder"
              className="w-full h-full object-cover object-top"
            />
          </div>
          <div>
            <div className="overline">Our story</div>
            <h2 className="font-serif text-4xl md:text-5xl mt-3 tracking-tight">A heritage stitched in every print.</h2>
            <p className="mt-6 text-base leading-relaxed text-brand-muted">
              Cindy Marks Collections began with a single bolt of Ankara fabric and a vision: clothing that honors African craft while feeling unmistakably now. Every garment is cut by hand in small runs, so each piece carries the maker's signature.
            </p>
            <Link to="/about" data-testid="about-learn-more" className="btn-outline mt-8">Learn more</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
